import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Merchant } from '@galaxy/billing';
import { TranslateModule } from '@ngx-translate/core';
import { EditContactInfoDialogComponent } from '@vendasta/billing-ui';
import { combineLatest, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { PartnerService } from '../../core/partner.service';
import { BillingSettingsService } from './billing-settings.service';

@Component({
  selector: 'app-billing-contact-info',
  styleUrls: ['./contact-info.component.scss'],
  template: `
    <div *ngIf="data$ | async as data; else processing">
      <div *ngIf="data.contactInfo.merchantId && data.isMerchantConfigured">
        <div class="row row-gutters">
          <b class=" col col-xs-6 client">{{ data.contactInfo.companyName }}</b>
          <a *ngIf="editable" (click)="openEditModal()" class="col col-xs-6 edit-link">
            <span *ngIf="data.hasFailed">{{ 'COMMON.ACTION_LABELS.ADD' | translate | uppercase }}</span>
            <span *ngIf="!data.hasFailed">{{ 'COMMON.ACTION_LABELS.EDIT' | translate | uppercase }}</span>
          </a>
        </div>
        <div class="row row-gutters card-data">
          <div class="col col-xs-6 address-column">
            {{ data.contactInfo.contactName }}
            <br />
            {{ data.contactInfo.address }}
            <br />
            {{ data.contactInfo.city }} {{ data.contactInfo.state }}
            <br />
            {{ data.contactInfo.country }}
            {{ data.contactInfo.zipCode }}
            <br />
          </div>
          <div class="col col-xs-6 address-column">
            {{ data.contactInfo.emailAddress }}
            <br />
            {{ data.contactInfo.phoneNumber }}
          </div>
        </div>
      </div>
      <div *ngIf="!data.isMerchantConfigured" class="card-data">
        <div class="row row-gutters">
          <div class="col col-xs-10">{{ 'ACCOUNT_BILLING_SETTINGS.NO_CONTACT_CONFIGURED' | translate }}</div>
          <a *ngIf="editable" (click)="openEditModal()" class="col col-xs-2 edit-link">
            <span *ngIf="data.hasFailed">{{ 'COMMON.ACTION_LABELS.ADD' | translate | uppercase }}</span>
            <span *ngIf="!data.hasFailed">{{ 'COMMON.ACTION_LABELS.EDIT' | translate | uppercase }}</span>
          </a>
        </div>
      </div>
    </div>
    <ng-template #processing>
      <div class="spinner">
        <mat-spinner [strokeWidth]="3" [diameter]="24"></mat-spinner>
      </div>
    </ng-template>
  `,
  imports: [CommonModule, MatProgressSpinnerModule, TranslateModule],
})
export class ContactInfoComponent implements OnInit {
  @Input() editable = false;
  @Input() merchantId: string;
  data$: Observable<any>;

  constructor(
    private billingSettingsService: BillingSettingsService,
    private dialog: MatDialog,
    private partnerService: PartnerService,
  ) {}

  ngOnInit(): void {
    if (this.merchantId) {
      this.billingSettingsService.merchantId = this.merchantId;
    } else if (!this.billingSettingsService.merchantId) {
      this.billingSettingsService.merchantId = this.partnerService.partnerId;
    }

    this.billingSettingsService.loadMerchantDetails();
    this.data$ = combineLatest([
      this.billingSettingsService.merchantDetailsInfo,
      this.billingSettingsService.failed,
      this.billingSettingsService.isMerchantConfigured,
    ]).pipe(
      map(([contactInfo, hasFailed, isMerchantConfigured]) => {
        return { contactInfo, hasFailed, isMerchantConfigured };
      }),
    );
  }

  public openEditModal(): void {
    this.billingSettingsService.merchantDetailsInfo.pipe(take(1)).subscribe((contactInfo) => {
      const dialogRef = this.dialog.open(EditContactInfoDialogComponent, {
        width: '500px',
        data: {
          merchantId: this.merchantId,
          merchant: contactInfo.merchantId !== '' ? (contactInfo as Merchant) : null,
        },
      });
      dialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe((merchantUpdated) => {
          if (merchantUpdated) {
            this.billingSettingsService.loadMerchantCreditCards();
          }
          this.billingSettingsService.loadMerchantDetails();
        });
    });
  }
}
